import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCasesQuery, type CaseStatus, type CasesListResponse } from "./useLabelingQueries";

export const isFinishedCaseStatus = (status?: string) =>
  status === "complete" || status === "unassessable" || status === "excluded";

// A technical hold is unfinished, but waits on the study team: skip it when navigating.
export const isSkippedByNavigation = (status?: string) =>
  isFinishedCaseStatus(status) || status === "technical_hold";

/**
 * Round-robin choice of the next case to annotate: prefer incomplete cases with
 * the fewest annotations so every case gets at least 1 before any gets a 2nd,
 * taking the first one after the current position (wrapping around).
 */
export function nextUnlabeledIndex(
  studies: Array<{ case_id: string }>,
  cases: CaseStatus[] | undefined,
  currentIndex: number,
): number | null {
  if (!cases) return null;
  const caseMap = new Map(cases.map((c) => [c.case_id, c]));
  const incomplete: { idx: number; count: number }[] = [];
  studies.forEach((study, idx) => {
    const caseStatus = caseMap.get(study.case_id);
    if (!caseStatus || !isSkippedByNavigation(caseStatus.completion_status)) {
      incomplete.push({ idx, count: caseStatus?.annotation_count ?? 0 });
    }
  });
  if (incomplete.length === 0) return null;
  const minCount = Math.min(...incomplete.map((c) => c.count));
  const minCases = incomplete.filter((c) => c.count === minCount);
  return (minCases.find((c) => c.idx > currentIndex) ?? minCases[0]).idx;
}

export function useNextUnlabeledCase(
  projectId: string,
  studies: Array<{ case_id: string }> | undefined,
  currentIndex: number,
  onNavigate: (index: number) => void,
) {
  const { data: casesData } = useCasesQuery(projectId, "all");
  const queryClient = useQueryClient();
  const hasNextUnlabeled =
    !!studies && nextUnlabeledIndex(studies, casesData?.cases, currentIndex) !== null;
  const goToNextUnlabeled = useCallback(() => {
    if (!studies) return;
    // Read the cache at navigation time: Exclude & Next refetches cases just
    // before advancing, and this callback may come from the render before it.
    const fresh = queryClient.getQueryData<CasesListResponse>(["project-cases", projectId, "all"]);
    const nextIdx = nextUnlabeledIndex(studies, fresh?.cases ?? casesData?.cases, currentIndex);
    if (nextIdx !== null) onNavigate(nextIdx);
  }, [queryClient, projectId, studies, casesData, currentIndex, onNavigate]);
  return { hasNextUnlabeled, goToNextUnlabeled };
}
